import { CoveragePlanType } from '@cms-max/medicare-api-sdk'
import { IconCheckShield } from 'components/Icon/IconCheckShield'
import { MyInternalLink, MyLink, MyLinkClickHandler } from 'components/MyLink'
import { useEvent } from 'hooks/useEvent'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { usePlansQuery } from 'queries/plans'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { NoPlansCard } from '../NoPlansCard/NoPlansCard'
import { PartAPartBPlanCard } from '../PlanCard/PartAPartBPlanCard'
import { PlanCard } from '../PlanCard/PlanCard'
import { PlanCardSkeleton } from '../PlanCard/PlanCardSkeleton'
import { PlansErrorAlert } from '../PlansErrorAlert/PlansErrorAlert'

import classes from './CurrentPlans.module.scss'
import useCombineAndFilterPlans from './useCombineAndFilterPlans'
import { useFilterPlans } from './useFilterPlans'

type CurrentPlansProps = {
  className?: string
}
export const CurrentPlans = ({ className }: CurrentPlansProps) => {
  const { t } = useTranslation('accountHome')
  const { fireEvent } = useEvent('accountHome')

  const flags = useFlags()

  const { data: planData, isError, isLoading } = usePlansQuery()

  const { current, future } = useFilterPlans(planData?.plans)

  // This governs display of alternate messaging if there are no current plans for the user
  const noPlans =
    !isLoading &&
    !isError &&
    !current.length &&
    (!flags?.accountHomePlansFuture || !future.length)

  // This filters the current plans in order to combine OM Part A & B w/o dupes
  const { omCombined: omCombinedCurrent, filteredPlans: filteredPlansCurrent } =
    useCombineAndFilterPlans({ plans: current })

  const { filteredPlans: filteredPlansFuture, omCombined: futureOmCombined } =
    useCombineAndFilterPlans({
      plans: future
    })

  const planSummaryLink = useMedicareUrl(
    'https://www.medicare.gov/plan-compare/#/',
    'https://www.medicare.gov/plan-compare/#/?lang=es'
  )

  const legacyPlanDetailsLink = useMedicareUrl(
    'https://www.medicare.gov/mbp/plansandcoverage.aspx',
    'https://www.medicare.gov/mbp/plansandcoverage.aspx?Lang=es-mx'
  )

  const planCardEventHandler = useCallback(
    function (planType: CoveragePlanType | 'om'): MyLinkClickHandler {
      return ({ linkText, linkUrl }) => {
        fireEvent('accounthome_plancard_click', {
          linkText,
          linkUrl,
          planType
        })
      }
    },
    [fireEvent]
  )

  const hasPlansToDisplay =
    omCombinedCurrent ||
    futureOmCombined ||
    Boolean(filteredPlansCurrent?.length) ||
    Boolean(filteredPlansFuture?.length)

  return (
    <section className={`account-home-section current-plans ${className}`}>
      <div className="icon">
        <IconCheckShield ariaHidden />
      </div>
      <h2 className="ds-text-heading--xl" id="plans">
        {flags?.accountHomePlansFuture
          ? t('currentPlans.heading')
          : t('currentPlans.headingCurrentPlans')}
      </h2>
      <div className="content my-u-pb--32">
        {noPlans
          ? (
          <NoPlansCard className={classes.statusCard} />
            )
          : (
          <div>
            {isLoading && (
              <div className={classes.grid}>
                {Array.from({ length: 3 }).map((x, i) => (
                  <PlanCardSkeleton key={i} />
                ))}
              </div>
            )}
            {isError && <PlansErrorAlert className={classes.statusCard} />}

            {hasPlansToDisplay && (
              <div className={classes.grid}>
                {omCombinedCurrent && (
                  <PartAPartBPlanCard
                    partAEffectiveDate={omCombinedCurrent.partAEffectiveDate}
                    partBEffectiveDate={omCombinedCurrent.partBEffectiveDate}
                    urls={omCombinedCurrent.urls}
                    onLinkClick={planCardEventHandler('om')}
                    key={'currentOmCombined'}
                  />
                )}

                {[...filteredPlansCurrent].map((plan) => (
                  <PlanCard
                    name={plan.name}
                    startDate={plan.enrollment_effective_date}
                    title={plan.title}
                    type={plan.type}
                    urls={plan.urls}
                    rxCard={plan.rxCard}
                    status={plan.status}
                    onLinkClick={planCardEventHandler(plan.type)}
                    key={plan.contract_id}
                  />
                ))}

                {futureOmCombined && !omCombinedCurrent && (
                  <PartAPartBPlanCard
                    partAEffectiveDate={futureOmCombined.partAEffectiveDate}
                    partBEffectiveDate={futureOmCombined.partBEffectiveDate}
                    urls={futureOmCombined.urls}
                    onLinkClick={planCardEventHandler('om')}
                    key={'futureOmCombined'}
                  />
                )}

                {[
                  ...(flags?.accountHomePlansFuture ? filteredPlansFuture : [])
                ].map((plan) => (
                  <PlanCard
                    name={plan.name}
                    startDate={plan.enrollment_effective_date}
                    title={plan.title}
                    type={plan.type}
                    urls={plan.urls}
                    onLinkClick={planCardEventHandler(plan.type)}
                    rxCard={plan.rxCard}
                    status={plan.status}
                    key={plan.contract_id}
                  />
                ))}
              </div>
            )}
          </div>
            )}

        {!isLoading && !noPlans && (
          <div className="ds-u-margin-top--3 ds-u-display--flex my-u-gap--8 ds-u-flex-direction--column ds-u-md-flex-direction--row">
            <MyLink
              className="ds-c-button ds-c-button--solid ds-c-button--alternate ds-u-display--block ds-u-md-display--inline-block"
              href={planSummaryLink}
              onClick={({ linkText, linkUrl }) =>
                fireEvent('accounthome_checkplansummary_click', {
                  linkText,
                  linkUrl
                })
              }
            >
              {t('currentPlans.planSummaryLink')}
            </MyLink>

            {!flags?.accountHomePlansHistory && (
              <MyLink
                className="ds-c-button ds-u-display--block ds-u-md-display--inline-block"
                href={
                  flags.accountHomePlansFuture
                    ? '#plans'
                    : legacyPlanDetailsLink
                }
                onClick={({ linkText, linkUrl }) =>
                  fireEvent('accounthome_getplandetails_click', {
                    linkText,
                    linkUrl
                  })
                }
              >
                {t('currentPlans.planDetailsLink')}
              </MyLink>
            )}
            {flags?.accountHomePlansHistory && (
              <MyInternalLink
                className="ds-c-button ds-u-display--block ds-u-md-display--inline-block"
                to={`/home/planHistory`}
                onClick={({ linkText, linkUrl }) =>
                  fireEvent('accounthome_reviewplanhistory_click', {
                    linkText,
                    linkUrl
                  })
                }
              >
                {t('currentPlans.planHistoryLink')}
              </MyInternalLink>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
